import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { findLeader } from "@/data/content";
import { pickLocale } from "@/i18n";

export default function LeaderDetail() {
  useReveal();
  const { t } = useTranslation();
  const { slug } = useParams();
  const leader = findLeader(slug);

  if (!leader) {
    return (
      <div className="min-h-screen bg-white text-unik-ink">
        <SiteNav />
        <div className="mx-auto max-w-[1200px] px-6 py-40 text-center">
          <h1 className="font-display text-4xl text-[#14355a]">{t("leaderDetail.notFound")}</h1>
          <Link to="/about/leadership" className="link-rule mt-8 inline-block text-[#14355a] text-sm">{t("leaderDetail.backToLeadership")}</Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const name = pickLocale(leader, "name");
  const role = pickLocale(leader, "role");
  const overview = pickLocale(leader, "overview") || leader.overview;
  const education = pickLocale(leader, "education") || leader.education;
  const experience = pickLocale(leader, "experience") || leader.experience;

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <section className="relative bg-[#14355a] text-white pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 silk-bg opacity-90" />
        <div className="absolute inset-0 grain pointer-events-none opacity-[0.05]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-5" data-reveal>
            <div className="aspect-[4/5] overflow-hidden bg-white/5">
              <img src={leader.img} alt={name} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Link to="/about/leadership" data-reveal className="inline-flex items-center gap-2 text-white/80 text-xs uppercase tracking-[0.2em] font-display hover:text-unik-gold transition-colors mb-10">
              <ArrowLeft size={14} /> {t("leaderDetail.backToLeadership")}
            </Link>
            <div data-reveal style={{ "--reveal-delay": "100ms" }} className="eyebrow text-unik-gold">{t("leaderDetail.leadershipTeam")}</div>
            <h1
              data-reveal
              style={{ "--reveal-delay": "200ms", fontSize: "clamp(2.5rem, 5vw, 4.5rem)", fontWeight: 500, lineHeight: "1.0" }}
              className="mt-6 font-display tracking-tight"
            >
              {name}
            </h1>
            <p
              data-reveal
              className="mt-5 text-[14px] lg:text-[15px] text-white/85 tracking-wide"
              style={{ "--reveal-delay": "320ms", fontWeight: 500 }}
            >
              {role}
            </p>
            <div
              data-reveal
              style={{ "--reveal-delay": "440ms" }}
              className="mt-10 max-w-2xl space-y-5 text-[17px] lg:text-[18px] leading-[1.95] text-white/85"
            >
              {overview.slice(0, 2).map((p, i) => (
                <p key={`hero-overview-${i}-${p.slice(0, 24)}`}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {overview.length > 2 && (
        <section className="relative bg-white py-24 lg:py-28">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="lg:col-span-4" data-reveal>
              <div className="eyebrow text-[#14355a]">{t("leaderDetail.biography")}</div>
              <div className="mt-6 w-12 h-px bg-unik-gold" />
            </div>
            <div className="lg:col-span-8 space-y-6 text-[17px] lg:text-[18px] leading-[1.95] text-unik-ink/80">
              {overview.slice(2).map((p, i) => (
                <p key={`overview-${i}-${p.slice(0, 24)}`} data-reveal style={{ "--reveal-delay": `${i * 90}ms` }}>{p}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="relative bg-unik-cream py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16">
          {education && education.length > 0 && (
            <div data-reveal>
              <div className="eyebrow text-[#14355a]">{t("leaderDetail.education")}</div>
              <div className="mt-6 w-12 h-px bg-unik-gold" />
              <ul className="mt-8 space-y-4">
                {education.map((e, i) => (
                  <li key={`edu-${i}-${e.slice(0, 24)}`} className="text-[16px] lg:text-[17px] leading-[1.85] text-unik-ink/80 border-l-2 border-[#14355a]/20 pl-5">
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {experience && experience.length > 0 && (
            <div data-reveal style={{ "--reveal-delay": "120ms" }}>
              <div className="eyebrow text-[#14355a]">{t("leaderDetail.experience")}</div>
              <div className="mt-6 w-12 h-px bg-unik-gold" />
              <ul className="mt-8 space-y-4">
                {experience.map((e, i) => (
                  <li key={`exp-${i}-${e.slice(0, 24)}`} className="text-[16px] leading-[1.85] text-unik-ink/80 border-l-2 border-[#14355a]/20 pl-5">
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <BottomCTA
        eyebrow={t("leaderDetail.ctaEyebrow")}
        title={t("leaderDetail.ctaTitle")}
        accent={t("leaderDetail.ctaAccent")}
        body={t("leaderDetail.ctaBody")}
        primary={{ label: t("common.contactUs"), to: "/contact" }}
        secondary={{ label: t("leaderDetail.backToLeadership"), to: "/about/leadership" }}
      />
      <SiteFooter />
    </div>
  );
}
